import React, { useEffect, useState } from 'react'
import _, { isEmpty } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
  Switch,
  Tag,
  Typography
} from 'antd'
import { useDeepEffect } from '../../../../utils/hooks'
import { testSendGroup } from '../../../../modules/priceGroup'
import { getAllTierFeatureList } from '../../../../modules/tierFeature'
import {
  getTierAccessBySlug,
  grantTierAccess,
  revokeTierAccess
} from '../../../../modules/priceGroupTierAccess'
import {
  parseCron,
  buildCron,
  describeCron
} from '../../../PriceManagement/components/ScheduleForm'

const { Option } = Select
const { Text } = Typography

// Tier changes are buffered locally (tiers/onAdd/onRemove) and only
// committed to the API when the group form's "บันทึก" is clicked — avoids
// silently writing to the DB while the rest of the group form is unsaved.
const TierAccessSection = ({
  tiers,
  tierOptions,
  loading,
  onAdd,
  onRemove
}) => {
  const [adding, setAdding] = useState(false)

  const availableTiers = tierOptions.filter((tier) => !tiers.includes(tier))

  const onSelectTier = (tier) => {
    setAdding(false)
    onAdd(tier)
  }

  return (
    <React.Fragment>
      <Divider orientation="left" style={{ fontSize: 14 }}>
        Tier Access
      </Divider>
      <Spin spinning={loading}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            minHeight: 32
          }}>
          {tiers.map((tier) => (
            <Tag
              key={tier}
              color="geekblue"
              closable
              style={{ marginBottom: 8 }}
              onClose={() => onRemove(tier)}>
              {tier}
            </Tag>
          ))}
          {adding ? (
            <Select
              size="small"
              autoFocus
              defaultOpen
              style={{ width: 140, marginBottom: 8 }}
              placeholder="เลือก tier"
              onChange={onSelectTier}>
              {availableTiers.map((tier) => (
                <Option key={tier} value={tier}>
                  {tier}
                </Option>
              ))}
            </Select>
          ) : (
            <Button
              size="small"
              icon="plus"
              disabled={availableTiers.length === 0}
              style={{ marginBottom: 8 }}
              onClick={() => setAdding(true)}>
              Tier
            </Button>
          )}
        </div>
      </Spin>
    </React.Fragment>
  )
}

const TIMEZONES = [
  'Asia/Bangkok',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'UTC',
  'America/New_York',
  'Europe/London'
]

const DEFAULT_CRON_STATE = {
  mode: 'daily',
  minutes: 15,
  hours: 1,
  time: '09:00',
  raw: ''
}

const GroupForm = (props) => {
  const { visible, onOk, onCancel, formData, form } = props
  const { getFieldDecorator, validateFields, setFieldsValue } = form

  const dispatch = useDispatch()
  const isTesting = useSelector((s) => s.priceGroup.isTesting)
  const tierOptions = useSelector(
    (s) => (s.tierFeature.tierFeatureList || []).map((row) => row.tier),
    []
  )

  const [lineEnabled, setLineEnabled] = useState(false)
  const [cronState, setCronState] = useState(DEFAULT_CRON_STATE)
  const [pendingTiers, setPendingTiers] = useState([])
  const [originalTiers, setOriginalTiers] = useState([])
  const [tierSeeded, setTierSeeded] = useState(false)

  const isEditing = !_.isEmpty(formData)
  const canTestSend = isEditing && !!formData.discord_webhook_url

  const onTestSend = () => {
    if (!canTestSend) return
    Modal.confirm({
      title: 'ส่ง Discord snapshot ตอนนี้?',
      content: `จะส่งราคาปัจจุบันของกลุ่ม "${formData.name}" ไป Discord webhook ที่บันทึกไว้`,
      okText: 'ส่งเลย',
      cancelText: 'ยกเลิก',
      onOk: () => dispatch(testSendGroup(formData.id))
    })
  }

  useDeepEffect(() => {
    // Always clear form fields first — antd's Form.create() keeps field state
    // alive across reopens (even with Modal destroyOnClose, certain race paths
    // leave stale values). Reset, then populate when editing.
    form.resetFields()
    if (!isEmpty(formData)) {
      setFieldsValue({
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        discord_webhook_url: formData.discord_webhook_url,
        timezone: formData.timezone || 'Asia/Bangkok',
        line_keyword: formData.line_keyword
      })
      setLineEnabled(!!formData.line_enabled)
      setCronState(
        formData.discord_cron_expression
          ? parseCron(formData.discord_cron_expression)
          : DEFAULT_CRON_STATE
      )
    } else {
      setLineEnabled(false)
      setCronState(DEFAULT_CRON_STATE)
    }

    return () => {
      form.resetFields()
    }
  }, [formData, visible])

  useEffect(() => {
    let cancelled = false
    if (!isEmpty(formData) && formData.slug) {
      setTierSeeded(false)
      dispatch(getAllTierFeatureList())
      dispatch(getTierAccessBySlug(formData.slug)).then((tiers) => {
        if (cancelled) return
        const list = tiers || []
        setOriginalTiers(list)
        setPendingTiers(list)
        setTierSeeded(true)
      })
    } else {
      setPendingTiers([])
      setOriginalTiers([])
      setTierSeeded(false)
    }
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.slug, visible])

  const onAddTier = (tier) => setPendingTiers((prev) => [...prev, tier])
  const onRemoveTier = (tier) =>
    setPendingTiers((prev) => prev.filter((t) => t !== tier))

  const updateCron = (patch) => setCronState((s) => ({ ...s, ...patch }))

  const onSubmit = (e) => {
    e.preventDefault()
    validateFields(async (err, values) => {
      if (err) return

      const webhook = (values.discord_webhook_url || '').trim()
      const cron = webhook ? buildCron(cronState) : ''

      if (webhook && !cron) {
        Modal.error({
          title: 'กรุณาตั้งเวลาส่ง Discord',
          content: 'เมื่อระบุ Discord Webhook แล้ว ต้องตั้งเวลาส่งด้วย'
        })
        return
      }

      const rawSlug = (values.slug || '').trim().toLowerCase()
      const data = {
        name: values.name,
        slug: rawSlug || null,
        description: values.description || null,
        discord_webhook_url: webhook || null,
        discord_cron_expression: cron || null,
        timezone: values.timezone || 'Asia/Bangkok',
        line_enabled: lineEnabled,
        line_keyword: lineEnabled ? values.line_keyword || null : null
      }
      if (!_.isEmpty(formData)) {
        data.id = formData.id
      }

      if (isEditing && formData.slug) {
        const added = pendingTiers.filter((t) => !originalTiers.includes(t))
        const removed = originalTiers.filter((t) => !pendingTiers.includes(t))
        await Promise.all([
          ...added.map((t) => dispatch(grantTierAccess(t, formData.slug))),
          ...removed.map((t) => dispatch(revokeTierAccess(t, formData.slug)))
        ])
      }

      onOk(data)
    })
  }

  const cronPreview = buildCron(cronState)

  return (
    <Modal
      title={`${!_.isEmpty(formData) ? 'แก้ไข' : 'เพิ่ม'} กลุ่มลูกค้า`}
      visible={visible}
      footer={null}
      onCancel={onCancel}
      width={640}
      destroyOnClose>
      <Form onSubmit={onSubmit} layout="vertical">
        <Form.Item label="ชื่อกลุ่ม">
          {getFieldDecorator('name', {
            rules: [{ required: true, message: 'กรุณากรอกชื่อกลุ่ม' }]
          })(<Input placeholder="เช่น ลูกค้า VIP" />)}
        </Form.Item>

        <Form.Item
          label="Slug (สำหรับ API)"
          extra="ตัวระบุกลุ่มสำหรับ service-to-service API เช่น 'basic', 'premium' — a-z, 0-9, ' - ' เท่านั้น (เว้นว่าง = ไม่เปิดให้ API ตัวอื่นเรียก)">
          {getFieldDecorator('slug', {
            rules: [
              {
                pattern: /^[a-z0-9][a-z0-9-]{0,49}$/,
                message: 'a-z, 0-9, "-" เท่านั้น (ตัวแรกเป็นตัวอักษรหรือตัวเลข)'
              }
            ]
          })(<Input placeholder="basic" autoComplete="off" />)}
        </Form.Item>

        <Form.Item label="คำอธิบาย">
          {getFieldDecorator('description')(
            <Input.TextArea rows={2} placeholder="อธิบายสั้นๆ (ไม่บังคับ)" />
          )}
        </Form.Item>

        {isEditing && formData.slug ? (
          <TierAccessSection
            tiers={pendingTiers}
            tierOptions={tierOptions}
            loading={!tierSeeded}
            onAdd={onAddTier}
            onRemove={onRemoveTier}
          />
        ) : (
          <React.Fragment>
            <Divider orientation="left" style={{ fontSize: 14 }}>
              Tier Access
            </Divider>
            <Text type="secondary">
              ต้องบันทึกกลุ่มพร้อม slug ก่อน ถึงจะติด tag tier ได้
            </Text>
          </React.Fragment>
        )}

        <Divider orientation="left" style={{ fontSize: 14 }}>
          Discord
        </Divider>

        <Alert
          message="ถ้าระบุ Webhook ต้องตั้งเวลาส่งด้วย (ไม่ใส่ทั้งคู่ = ไม่ส่ง Discord)"
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
        />

        <Form.Item label="Discord Webhook URL">
          {getFieldDecorator('discord_webhook_url')(
            <Input placeholder="https://discord.com/api/webhooks/..." />
          )}
        </Form.Item>

        <Form.Item label="ความถี่การส่ง">
          <Radio.Group
            value={cronState.mode}
            onChange={(e) => updateCron({ mode: e.target.value })}>
            <Radio.Button value="minutes">ทุก X นาที</Radio.Button>
            <Radio.Button value="hours">ทุก X ชั่วโมง</Radio.Button>
            <Radio.Button value="daily">รายวัน</Radio.Button>
            <Radio.Button value="custom">กำหนดเอง</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {cronState.mode === 'minutes' && (
          <Form.Item label="ทุกกี่นาที (1-59)">
            <InputNumber
              min={1}
              max={59}
              value={cronState.minutes}
              onChange={(v) => updateCron({ minutes: v || 1 })}
            />
          </Form.Item>
        )}
        {cronState.mode === 'hours' && (
          <Form.Item label="ทุกกี่ชั่วโมง (1-23)">
            <InputNumber
              min={1}
              max={23}
              value={cronState.hours}
              onChange={(v) => updateCron({ hours: v || 1 })}
            />
          </Form.Item>
        )}
        {cronState.mode === 'daily' && (
          <Form.Item label="เวลา">
            <Input
              type="time"
              value={cronState.time}
              onChange={(e) => updateCron({ time: e.target.value })}
              style={{ width: 160 }}
            />
          </Form.Item>
        )}
        {cronState.mode === 'custom' && (
          <Form.Item
            label="Cron Expression"
            extra="รูปแบบ: min hour dayOfMonth month dayOfWeek (เช่น 0 9 * * *)">
            <Input
              value={cronState.raw}
              onChange={(e) => updateCron({ raw: e.target.value })}
              placeholder="0 9 * * *"
            />
          </Form.Item>
        )}

        <Form.Item label="สรุปเวลาส่ง">
          <div>
            <Text strong>{describeCron(cronPreview)}</Text>
          </div>
          <Text
            type="secondary"
            style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {cronPreview || '—'}
          </Text>
        </Form.Item>

        <Form.Item label="Timezone">
          {getFieldDecorator('timezone', {
            initialValue: 'Asia/Bangkok'
          })(
            <Select>
              {TIMEZONES.map((tz) => (
                <Option key={tz} value={tz}>
                  {tz}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Divider orientation="left" style={{ fontSize: 14 }}>
          LINE
        </Divider>

        <Form.Item label="เปิดใช้งาน LINE">
          <Switch
            checked={lineEnabled}
            onChange={(checked) => setLineEnabled(checked)}
          />
        </Form.Item>

        {lineEnabled && (
          <Form.Item
            label="Keyword"
            extra="ผู้ใช้พิมพ์ keyword นี้ใน LINE เพื่อขอราคาของกลุ่ม">
            {getFieldDecorator('line_keyword')(
              <Input placeholder="เช่น ราคาทองแดง" />
            )}
          </Form.Item>
        )}

        <Row type="flex" gutter={8} justify="space-between" align="middle">
          <Col>
            {canTestSend && (
              <Button icon="send" loading={isTesting} onClick={onTestSend}>
                ส่งทันที (ทดสอบ Discord)
              </Button>
            )}
          </Col>
          <Col>
            <Row type="flex" gutter={8}>
              <Col>
                <Button onClick={() => onCancel()}>ยกเลิก</Button>
              </Col>
              <Col>
                <Button type="primary" htmlType="submit">
                  บันทึก
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

const WrappedGroupForm = Form.create({ name: 'GroupForm' })(GroupForm)
export default WrappedGroupForm
